import {
  Box,
  Paper,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Logo from "./components/logo/Logo";
import { Container, Stack } from "@mui/system";
import LanguageSelector from "./components/language-selector/LanguagSelector";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import CompanyInformation from "./views/company-information/CompanyInformation";
import ContactDetails from "./views/contact-details/ContactDetails";
import AccountVerification from "./views/account-verification/AccountVerification";
import DevToolbar from "./components/dev-toolbar/DevToolbar";
import PaymentMethod from "./views/payment-method/PaymentMethod";
import RegistrationConfirmation from "./views/registration-confirmation/RegistrationConfirmation";
import Loader from "./views/loader/Loader";
import {
  CompletedStep,
  calculateView,
  productRequiresPayment,
  steps,
  calculateStep,
  getQueryParam,
} from "./App.utils";
import Hubspot from "./components/hubspot/Hubspot";
import useQueryParameter from "./hooks/use-query-parameter";
import RedirectToApp from "./views/redirect-to-app/RedirectToApp";
import StepsDescriptionView from "./views/steps-description/StepsDescriptionView";

const EntityPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6, 4, 3, 4),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4, 2, 3, 2),
  },
}));

const StepperContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(0, 4, 0, 4),
  maxWidth: 360,
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: theme.spacing(4),
}));

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: "50%",
    transform: "translateY(-50%)",
  },
}));

const getCountryQueryParam = () => getQueryParam<string>("country");

function App() {
  const initialSignupInformation: SignupInformation = {
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    companyId: "",
    country: getCountryQueryParam() ?? "",
    companyName: "",
    streetAddress: "",
    zip: "",
    city: "",
    vat: "",
    parent: "",
    signUpComment: "",
  };
  const [completedStep, setCompletedStep] = useState<CompletedStep>("none");
  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [language, setLanguage] = useQueryParameter<Language>("hl", "en");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { i18n, ready } = useTranslation();
  const [signupInformation, setSignupInformation] = useState<SignupInformation>(
    initialSignupInformation
  );
  const [productId] = useQueryParameter<string>("productId", "");

  useEffect(() => {
    void i18n.changeLanguage(language);
  }, [i18n, language]);

  if (!ready) {
    return null;
  }

  const view = calculateView(
    completedStep,
    productRequiresPayment(product),
    loggedIn
  );

  const stepName = view === "Loader" ? "ContactDetails" : view;

  const hasAccount = stepName === "RedirectToApp";

  return (
    <>
      {process.env.NODE_ENV === "development" && (
        <DevToolbar
          onLoadClick={() => {
            setProduct(undefined);
            setCompletedStep("none");
          }}
          onContactDetailsClick={(information: CompanyInformation) => {
            setSignupInformation({
              ...signupInformation,
              ...information,
            });
            setCompletedStep("load");
          }}
          onCompanyInformationClick={() => {
            setCompletedStep("contactDetails");
          }}
          onAccountVerificationClick={(contactDetails: ContactDetails) => {
            setSignupInformation({
              ...signupInformation,
              ...contactDetails,
            });
            setCompletedStep("companyInformation");
          }}
          onPaymentMethodClick={() => setCompletedStep("accountVerification")}
          onRegistrationConfirmationClick={() =>
            setCompletedStep("paymentMethod")
          }
        />
      )}
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          p: { xs: 3, md: 3 },
          m: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={{ xs: 4, md: 4 }}
        >
          <Logo />
          <LanguageSelector
            language={language}
            onChange={(language) => setLanguage(language)}
          />
        </Stack>
        <Stack
          direction="row"
          sx={{ height: "100%" }}
          justifyContent="center"
          spacing={{ sm: 2, md: 3, lg: 6 }}
        >
          <EntityPaper
            sx={{
              flex: { xs: "1 1 auto", md: "0 0 auto" },
              width: { xs: "100%", sm: "100%", md: 450 },
              minHeight: 500,
            }}
          >
            <StepperContainer>
              <Stepper
                activeStep={calculateStep(completedStep)}
                alternativeLabel
                connector={<CustomConnector />}
              >
                {steps(!product, productRequiresPayment(product)).map(
                  (_, i) => (
                    <Step key={i}>
                      <StepLabel />
                    </Step>
                  )
                )}
              </Stepper>
            </StepperContainer>
            <Stack>
              {view === "Loader" && (
                <Loader
                  language={language}
                  productId={productId}
                  onComplete={(product: Product, isUserLoggedIn: boolean) => {
                    setProduct(product);
                    setCompletedStep("load");
                    setLoggedIn(isUserLoggedIn);
                  }}
                />
              )}
              {view === "ContactDetails" && (
                <ContactDetails
                  language={language}
                  productId={product?.id ?? ""}
                  details={signupInformation}
                  product={product}
                  onSubmit={(information: ContactDetails) => {
                    setSignupInformation({
                      ...signupInformation,
                      ...information,
                    });
                    setCompletedStep("contactDetails");
                  }}
                />
              )}
              {view === "CompanyInformation" && (
                <CompanyInformation
                  country={signupInformation.country}
                  signupInformation={signupInformation}
                  product={product}
                  language={language}
                  onBack={() => setCompletedStep("load")}
                  onSubmit={(information) => {
                    setSignupInformation({
                      ...signupInformation,
                      ...information,
                    });
                    setCompletedStep("companyInformation");
                  }}
                />
              )}
              {view === "AccountVerification" && (
                <AccountVerification
                  email={signupInformation.email}
                  password={signupInformation.password}
                  onSubmit={() => {
                    setCompletedStep("accountVerification");
                  }}
                />
              )}
              {view === "PaymentMethod" && (
                <PaymentMethod
                  language={language}
                  signupInformation={signupInformation}
                  onSubmit={() => {
                    setCompletedStep("paymentMethod");
                  }}
                />
              )}
              {view === "RegistrationConfirmation" && (
                <RegistrationConfirmation product={product} />
              )}
              {view === "RedirectToApp" && <RedirectToApp />}
            </Stack>
          </EntityPaper>
          {!isMobile && !hasAccount && (
            <Box flexGrow={1} maxWidth="700px" alignSelf="flex-start">
              <StepsDescriptionView
                stepName={stepName}
                name={signupInformation.companyName}
              />
            </Box>
          )}
        </Stack>
      </Container>
      <Hubspot id={process.env.REACT_APP_HUBSPOT_ID} />
    </>
  );
}

export default App;
